import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Row, Col, Form } from 'react-bootstrap'
import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import { BsSearch, BsArrowLeft, BsPersonFill, BsCalendar3, BsBookFill, BsTelephoneFill, BsEnvelopeFill } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const ViewAssignedStudents = () => {
  const { guardianId } = useParams()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name, id } = useInstitute()
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [guardian, setGuardian] = useState(null)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // Fetch courses and classes for display
  useEffect(() => {
    const fetchCoursesAndClasses = async () => {
      try {
        const institueId = Cookies.get('institute_id')
        const coursesRes = await axios.get(BASE_URL + '/course/all', {
          headers: {
            'institute-Id': Number(institueId),
          },
        })
        setCourses(coursesRes.data.data || [])

        const classesRes = await axios.get(BASE_URL + '/class/all', {
          headers: {
            'institute-Id': Number(institueId),
          },
        })
        setClasses(classesRes.data.data || [])
      } catch (error) {
        console.error('Error fetching courses/classes:', error)
      }
    }
    fetchCoursesAndClasses()
  }, [])

  // Fetch guardian info and assigned students
  useEffect(() => {
    if (guardianId) {
      fetchGuardianInfo()
      fetchAssignedStudents()
    }
  }, [guardianId])

  const fetchGuardianInfo = async () => {
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null

      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          token = user.token
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined,
        'institute-Id': Number(institueId),
      }

      const response = await axios.get(BASE_URL + `/parents/${guardianId}`, {
        headers,
      })
      setGuardian(response.data.data || response.data)
    } catch (error) {
      console.error('Error fetching guardian info:', error)
    }
  }

  const fetchAssignedStudents = async () => {
    if (!guardianId) return

    setLoading(true)
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null

      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          token = user.token
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined,
        'institute-Id': Number(institueId),
      }

      console.log('heeaders:', headers)
      // Try multiple endpoint patterns
      const endpoints = [
        `/parents/${guardianId}/students`
      ]

      let success = false
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(BASE_URL + endpoint, { headers })
          console.log('Response of assigned students:', response.data)
          if (response.data && (response.data.data || response.data.students)) {
            const studentsData =
              response.data.data || response.data.students || []
            setStudents(Array.isArray(studentsData) ? studentsData : [])
            success = true
            break
          }
        } catch (endpointError) {
          continue
        }
      }

      if (!success) {
        setStudents([])
        console.error('No students found for this guardian')
      }
    } catch (error) {
      console.error('Error fetching assigned students:', error)
      setStudents([])
      console.error(
        error.response?.data?.message ||
          'Failed to fetch assigned students',
      )
    } finally {
      setLoading(false)
    }
  }

  // Filter students based on search
  const filteredStudents = students.filter((student) =>
    Object.values(student).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(search.trim().toLowerCase()),
    ),
  )

  const getCourseName = (courseId) => {
    const course = courses.find((c) => c.id === courseId)
    return course?.name || 'N/A'
  }

  const getClassName = (classId) => {
    const classItem = classes.find((c) => c.id === classId)
    return classItem?.class_name || 'N/A'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  return (
    <>
      <style>
        {`
          .student-card {
            transition: all 0.3s ease;
            border: 1px solid #E5E7EB;
            border-radius: 12px;
            overflow: hidden;
            height: 100%;
          }
          .student-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
            border-color: #223843;
          }
          .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
          }
          .info-label {
            font-size: 12px;
            color: #6b7280;
            font-weight: 500;
            min-width: 100px;
          }
          .info-value {
            font-size: 14px;
            color: #111827;
            font-weight: 600;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              {/* Left:Title */}
              <div className="d-flex align-items-center gap-3">

                {/* School Logo */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <div>
                  <h3
                    className="m-0 fw-bold"
                    style={{
                      letterSpacing: '-0.02em',
                      overflow: 'visible',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Assigned Students
                  </h3>
                  {guardian && (
                    <p
                      className="m-0"
                      style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Guardian: {guardian.name || 'N/A'}
                    </p>
                  )}
                </div>
              </div>

              {/* Right: Profile Icon with Dropdown */}
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Search Bar */}
          <div className="mb-4">
            <div style={{ position: 'relative', maxWidth: '500px' }}>
              <BsSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                  fontSize: '16px',
                  zIndex: 1,
                }}
              />
              <input
                type="text"
                placeholder="Search students by name, admission number, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  paddingLeft: '40px',
                  borderRadius: '8px',
                  border: '1px solid #D1D3D4',
                  fontSize: '14px',
                  height: '48px',
                  width: '100%',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#ffffff',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#223843'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1D3D4'
                }}
              />
            </div>
          </div>

          {/* Students Count */}
          <div className="mb-4">
            <p
              style={{
                fontSize: '14px',
                color: '#6b7280',
                fontFamily: 'Inter, system-ui, sans-serif',
                margin: 0,
              }}
            >
              {loading
                ? 'Loading...'
                : `Showing ${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''}`}
            </p>
            <hr />
          </div>

          {/* Students Cards Grid */}
          {loading ? (
            <div
              className="text-center"
              style={{ padding: '60px', color: '#6b7280' }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: '#223843' }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading students...</p>
            </div>
          ) : filteredStudents.length > 0 ? (
            <Row className="g-4">
              {filteredStudents.map((student, index) => (
                <Col key={student.id || index} xs={12} sm={6} md={6} lg={4} xl={3}>
                  <Card className="student-card">
                    <Card.Body style={{ padding: '20px' }}>
                      {/* Student Name Header */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '20px',
                          paddingBottom: '16px',
                          borderBottom: '2px solid #F3F4F6',
                        }}
                      >
                        <div
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <BsPersonFill size={24} style={{ color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5
                            className="m-0 fw-bold"
                            style={{
                              fontSize: '16px',
                              color: '#111827',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {student.name || 'N/A'}
                          </h5>
                          <p
                            className="m-0"
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            #{student.admission_number || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Student Information */}
                      <div>
                        <div className="info-item">
                          <span className="info-label">
                            <BsPersonFill size={14} /> Father:
                          </span>
                          <span className="info-value">
                            {student.father_name || 'N/A'}
                          </span>
                        </div>

                        <div className="info-item">
                          <span className="info-label">
                            <BsBookFill size={14} /> Course:
                          </span>
                          <span className="info-value">
                            {getCourseName(student.course_id)}
                          </span>
                        </div>

                        <div className="info-item">
                          <span className="info-label">
                            <BsBookFill size={14} /> Class:
                          </span>
                          <span className="info-value">
                            {getClassName(student.class_id)}
                          </span>
                        </div>

                        <div className="info-item">
                          <span className="info-label">
                            <BsCalendar3 size={14} /> Admission:
                          </span>
                          <span className="info-value">
                            {formatDate(student.admission_date)}
                          </span>
                        </div>

                        {student.email && (
                          <div className="info-item">
                            <span className="info-label">
                              <BsEnvelopeFill size={14} /> Email:
                            </span>
                            <span
                              className="info-value"
                              style={{
                                fontSize: '12px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {student.email}
                            </span>
                          </div>
                        )}

                        {student.parents_number && (
                          <div className="info-item">
                            <span className="info-label">
                              <BsTelephoneFill size={14} /> Phone:
                            </span>
                            <span className="info-value">
                              {student.parents_number}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* View Profile Button */}
                      <button
                        onClick={() => navigate(`/student/profile/${student.id}`)}
                        style={{
                          width: '100%',
                          marginTop: '16px',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #223843',
                          backgroundColor: '#223843',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.borderColor = '#223843'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.borderColor = '#223843'
                        }}
                      >
                        View Profile
                      </button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card
              style={{
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                padding: '60px',
                textAlign: 'center',
                backgroundColor: '#ffffff',
              }}
            >
              <LuGraduationCap
                size={64}
                style={{ color: '#9ca3af', marginBottom: '16px' }}
              />
              <h5
                style={{
                  color: '#6b7280',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  marginBottom: '8px',
                }}
              >
                No Students Found
              </h5>
              <p
                style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  margin: 0,
                }}
              >
                {search
                  ? 'No students match your search criteria'
                  : 'No students are assigned to this guardian'}
              </p>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}

export default ViewAssignedStudents

